import { gql, useLazyQuery } from '@apollo/client';
import {
	Box,
	Button,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	SimpleGrid,
	Text,
	useDisclosure,
} from '@chakra-ui/react';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Formik } from 'formik';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/km';
import { useEffect, useState } from 'react';
import ReactExport from 'react-export-excel';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import c from '../../../../constant';
import useStore from '../../../../store';
import { mappingKhmerNumber } from '../../../../utils/market-development';
import SelectValueFormField from '../../../form/SelectValueFormField';

const Q_REPORTS = gql`
	query get($limit: Float!, $offset: Float!, $year: Float, $companyId: String, $companyType: InsuranceInstitutionsType) {
		getQuarterlySaleReports(
			pagerInput: { limit: $limit, offset: $offset }
			companyId: $companyId
			companyType: $companyType
			year: $year
			quarter: $quarter
		) {
			list {
				id
				company {
					name
					nameKhmer
					type
				}
				year
				quarter
				createdAt
				reportType
			}
		}
	}
`;

const validationSchema = Yup.object().shape({
	year: Yup.string().required('required'),
	quarter: Yup.string().required('required'),
});

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

export default function ExcelInsuranceSales() {
	moment.locale('en-gb');
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [dataList, setDataList] = useState([]);
	const [showError, setShowError] = useState(false);
	const [getQuarterlySaleReports, { loading, data }] = useLazyQuery(Q_REPORTS);

	useEffect(() => {
		if (data?.getQuarterlySaleReports?.list) {
			const formattedData = data?.getQuarterlySaleReports?.list?.map((item) => ({
				company: item?.company?.name || 'N/A',
				companyNameKh: item?.company?.nameKhmer || 'N/A',
				type: item?.company?.type || 'N/A',
				quarter: item?.quarter || 'N/A',
				year: item?.year || 'N/A',
				submittedAt: item?.createdAt ? moment(item?.createdAt).format('YYYY-MM-DD') : 'N/A',
				status: item?.reportType || 'N/A',
			}));
			setDataList(formattedData);
		}
	}, [data]);

	return (
		<>
			<Button
				isLoading={false}
				isDisabled={false}
				onClick={onOpen}
				leftIcon={<FontAwesomeIcon icon={faFileExcel} style={{ fontSize: 16 }} />}
				variant="solid"
				size="sm"
				borderRadius={c.borderRadius}
				border="1px solid #bdc3c7"
			>
				<Text fontSize="sm" fontWeight="semibold">
					{t('Export')}
				</Text>
			</Button>
			<Modal
				borderRadius={c.borderRadius}
				isOpen={isOpen}
				onClose={onClose}
				closeOnEsc={loading ? false : true}
				closeOnOverlayClick={loading ? false : true}
			>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius}>
					<ModalHeader>{t('Export')}</ModalHeader>
					<ModalCloseButton isDisabled={loading} />
					<ModalBody>
						<Formik
							validationSchema={validationSchema}
							initialValues={{ year: '' }}
							onSubmit={(values) => {
								getQuarterlySaleReports({
									variables: {
										limit: 3000,
										offset: 0,
										companyId: null,
										companyType: null,
										year: parseInt(values?.year),
										quarter: parseInt(values?.quarter),
									},
								});
							}}
						>
							{({ values, setFieldValue, ...props }) => {
								return (
									<Box pb="4">
										<Form>
											<SimpleGrid columns={1} spacing={4}>
												<SelectValueFormField
													{...props}
													placeholder={t('Year')}
													name="year"
													required={true}
													label="Year"
													options={[0].reduce((p, c) => {
														let lp = p;
														let i = 2021;
														while (i <= moment().year()) {
															lp.push({ label: lang === 'kh' ? mappingKhmerNumber(i) : i, value: i });
															i = i + 1;
														}
														return lp?.reverse();
													}, [])}
												/>
												<Flex mt={2} justifyContent="flex-end">
													<Button
														mr={2}
														colorScheme="gray"
														variant="solid"
														size="md"
														h="42px"
														w="128px"
														borderRadius={c.borderRadius}
														type="submit"
													>
														{t('Get Data')}
													</Button>
													<ExcelFile
														filename={`Annual sale report-${moment(values?.year, 'Y').format('YYYY')}-generated at ${moment().format(c.fileNameDateFormat)}`}
														element={
															<Button
																mr={2}
																colorScheme="gray"
																variant="solid"
																size="md"
																h="42px"
																w="128px"
																borderRadius={c.borderRadius}
																isDisabled={dataList.length === 0}
																onClick={() => {
																	if (dataList.length === 0) {
																		setShowError(true);
																	}
																}}
															>
																{t('Export')}
															</Button>
														}
													>
														<ExcelSheet data={dataList} name="Sale Report for Insurance Company">
															<ExcelColumn label="Company" value="company" />
															<ExcelColumn label="CompanyNameKH" value="companyNameKh" />
															<ExcelColumn label="Type" value="type" />
															<ExcelColumn label="Year" value="year" />
															<ExcelColumn label="Submitted At" value="submittedAt" />
															<ExcelColumn label="Status" value="status" />
															<ExcelColumn label="Gross Premium" value="grossPremium" />
															<ExcelColumn label="Monthly Contribution" value="monthlyContribution" />
														</ExcelSheet>
													</ExcelFile>

													{showError && <Text color="red.500">{t('No data found')}</Text>}
												</Flex>
											</SimpleGrid>
										</Form>
									</Box>
								);
							}}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}
